import { useCallback, useEffect, useMemo, useState } from 'react';
import { Form, message } from 'antd';
import type { DatePickerProps } from 'antd';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { RuleObject } from 'antd/es/form';
import dayjs from 'dayjs';
import type { Campaign, Client } from '@skyrise-eng/types';
import type { DefaultOptionType } from 'antd/es/select';
import { EditableMediaPlan, FormConfig, UseMediaPlanForm } from '../MediaPlanForm.types';
import {
  useCreateMediaPlanMutation,
  useUpdateMediaPlanMutation,
  useGetClientListQuery,
  useGetChannelsBudgetQuery,
  useGetCampaignListQuery,
  useGetTradingDealPercentQuery
} from '@/api';
import { API_DATE_FORMAT, NAMESPACE, PRIVATE_PATH } from '@/constants';
import { DEFAULT_CURRENCY, getCurrencySymbol } from '@/constants/currency';
import { DEFAULT_AGENCY_DISCOUNT_PERCENT, FORM_FIELD } from '../MediaPlanForm.constants';

const formatDate = (data?: DatePickerProps['value']): string => data?.format(API_DATE_FORMAT) || '';

const buildOptions = (list?: (Campaign | Client)[]): DefaultOptionType[] | undefined =>
  list?.map(({ _id, name }) => ({ value: _id, label: name }));

export const useMediaPlanForm: UseMediaPlanForm = ({ mediaPlan, close }) => {
  const navigate = useNavigate();
  const { t } = useTranslation(NAMESPACE.MEDIA_PLAN_PAGE);
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [updateMediaPlan, { data: updatedMediaPlan, isLoading: isMediaPlanUpdating }] =
    useUpdateMediaPlanMutation();
  const [createMediaPlan, { data: newMediaPlan, isLoading: isMediaPlanCreating }] =
    useCreateMediaPlanMutation();
  const { data: channelsBudget = 0 } = useGetChannelsBudgetQuery(
    { mediaPlanId: mediaPlan?._id },
    { skip: !mediaPlan }
  );
  const [form] = Form.useForm<EditableMediaPlan>();
  const { data: clients, isLoading: areClientsLoading } = useGetClientListQuery();
  const selectedClient = Form.useWatch('client', form);
  const selectedCampaign = Form.useWatch('campaign', form);
  const { data: campaigns, isLoading: areCampaignsLoading } = useGetCampaignListQuery(
    { client: selectedClient },
    { skip: !selectedClient }
  );
  const { data: tradingDealData } = useGetTradingDealPercentQuery(selectedClient ?? '', {
    skip: !selectedClient
  });

  const handleSubmit = useCallback(() => {
    form.validateFields().then((values) => {
      const { scheduleDate, campaignDates, buyingPoint: _buyingPoint, ...rest } = values as typeof values & { buyingPoint?: string };
      const body = {
        ...rest,
        // A cleared discount input yields null; the API expects a 0-100 rate.
        agencyDiscount: rest.agencyDiscount ?? 0,
        startDate: formatDate(campaignDates?.[0]),
        endDate: formatDate(campaignDates?.[1]),
        scheduleDate: formatDate(scheduleDate)
      };

      if (mediaPlan) {
        updateMediaPlan({ ...body, _id: mediaPlan._id });
      } else {
        createMediaPlan(body);
      }
    });
  }, [createMediaPlan, form, mediaPlan, updateMediaPlan]);

  useEffect(() => {
    if (newMediaPlan) {
      navigate(`${PRIVATE_PATH.MEDIA_PLAN}/${newMediaPlan._id}`);
      message.success({
        content: t('mediaPlanCreatedSuccessfully'),
        duration: 3
      });
    }
  }, [navigate, newMediaPlan, t]);

  useEffect(() => {
    if (updatedMediaPlan) {
      close();
      message.success({
        content: t('mediaPlanUpdatedSuccessfully'),
        duration: 3
      });
    }
  }, [t, updatedMediaPlan, close]);

  useEffect(() => {
    const campaign = campaigns?.find(({ _id }) => _id === selectedCampaign);

    if (campaign) {
      form.setFieldValue('totalBudget', campaign.totalBudget);
    }
  }, [campaigns, form, selectedCampaign]);

  useEffect(() => {
    form.resetFields(['campaign', 'totalBudget']);
  }, [form, selectedClient]);

  const validateBudget = useCallback(
    (_: RuleObject, value: number) => {
      if (mediaPlan && value < channelsBudget) {
        return Promise.reject(new Error(`${t('totalBudgetShouldBeGreater')} ${channelsBudget}${getCurrencySymbol(form.getFieldValue('currency'))}`));
      }
      return Promise.resolve();
    },
    [channelsBudget, form, mediaPlan, t]
  );

  const config: FormConfig = useMemo(() => {
    const requiredFieldMessage = t('requiredFieldMessage', { ns: NAMESPACE.COMMON });

    return {
      [FORM_FIELD.CAMPAIGN]: {
        label: t('campaign', { ns: NAMESPACE.COMMON }),
        name: 'campaign',
        rules: [{ required: true, whitespace: true, type: 'string', message: requiredFieldMessage }]
      },
      [FORM_FIELD.CLIENT]: {
        label: t('client', { ns: NAMESPACE.COMMON }),
        name: 'client',
        rules: [{ required: true, type: 'string', message: requiredFieldMessage }]
      },
      [FORM_FIELD.CAMPAIGN_DATES]: {
        label: t('campaignDates', { ns: NAMESPACE.COMMON }),
        name: 'campaignDates',
        rules: [{ required: true, type: 'array', message: requiredFieldMessage }]
      },
      [FORM_FIELD.SCHEDULE_DATE]: {
        label: t('scheduleDate', { ns: NAMESPACE.COMMON }),
        name: 'scheduleDate',
        rules: [{ required: true, type: 'object', message: requiredFieldMessage }]
      },
      [FORM_FIELD.TOTAL_BUDGET]: {
        label: t('totalBudget'),
        name: 'totalBudget',
        rules: [
          { required: true, type: 'number', message: requiredFieldMessage },
          { validator: validateBudget }
        ]
      },
      [FORM_FIELD.AGENCY_DISCOUNT]: {
        label: t('agencyDiscount'),
        name: 'agencyDiscount',
        rules: [{ type: 'number', min: 0, max: 100, message: t('agencyDiscountRangeMessage') }]
      },
      [FORM_FIELD.CURRENCY]: {
        label: t('currency'),
        name: 'currency',
        rules: [{ required: true, type: 'string', message: requiredFieldMessage }]
      }
    };
  }, [t, validateBudget]);

  const initialValues: Partial<EditableMediaPlan> = useMemo(() => {
    // New plan: default the agency discount rate to the common 15%. This is a starting
    // point only — the trader can change it, and it is never applied retroactively to
    // an existing plan (see the branch below, which always preserves the stored value).
    if (!mediaPlan) return { agencyDiscount: DEFAULT_AGENCY_DISCOUNT_PERCENT, currency: DEFAULT_CURRENCY };

    const { startDate, endDate, scheduleDate, client, campaign, agencyDiscount, tradingDeal: _tradingDeal, buyingPoint: _buyingPoint, buyingPointDealName: _bpdn, ...rest } = mediaPlan as typeof mediaPlan & { buyingPointDealName?: string; buyingPoint?: string };

    return {
      // Existing plan: always use the persisted numeric rate as-is (defaulting only
      // missing/null to 0), never overwrite it with the new-plan default.
      agencyDiscount: Number.isFinite(agencyDiscount) ? Number(agencyDiscount) : 0,
      campaign: campaign._id,
      client: client._id,
      campaignDates: [dayjs(startDate), dayjs(endDate)],
      scheduleDate: dayjs(scheduleDate),
      ...rest
    };
  }, [mediaPlan]);

  const tradingDealPercent =
    tradingDealData?.tradingDealPercent != null ? tradingDealData.tradingDealPercent : null;

  return {
    form,
    config,
    initialValues,
    clientOptions: buildOptions(clients),
    campaignOptions: buildOptions(campaigns),
    tradingDealPercent,
    areClientsLoading,
    areCampaignsLoading,
    isLoading: isMediaPlanCreating || isMediaPlanUpdating,
    setIsConfirmModalOpen,
    isConfirmModalOpen,
    handleSubmit,
    validateBudget
  };
};
